import { useMemo, useState } from "react";
import {
  Box,
  Tooltip,
  Typography,
  TooltipProps,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { multiSelectTooltipPopperProps } from "../FilterPanelStyles";

const PREVIEW_ITEM_LIMIT = 5;
const EXPANDED_LIST_MAX_HEIGHT = 220;

interface MultiSelectFilterTooltipProps {
  items: string[] | null | undefined;
  isMenuOpen: boolean;
  children: TooltipProps["children"];
  placement?: TooltipProps["placement"];
}

const MultiSelectFilterTooltipContent = ({ items }: { items: string[] }) => {
  const [expanded, setExpanded] = useState(false);
  const needsPreview = items.length > PREVIEW_ITEM_LIMIT;

  const visibleItems = useMemo(() => {
    if (!needsPreview || expanded) {
      return items;
    }

    return items.slice(0, PREVIEW_ITEM_LIMIT);
  }, [expanded, items, needsPreview]);

  const hiddenCount = items.length - PREVIEW_ITEM_LIMIT;

  return (
    <Box sx={{ maxWidth: 280, minWidth: 160 }}>
      {needsPreview && (
        <Typography
          sx={{
            fontSize: "0.7rem",
            fontWeight: 600,
            mb: 0.75,
            color: "common.white",
            opacity: 0.95,
          }}
        >
          {items.length} items selected
        </Typography>
      )}

      <Box
        sx={{
          maxHeight: expanded ? EXPANDED_LIST_MAX_HEIGHT : "none",
          overflowY: expanded ? "auto" : "visible",
          pr: expanded ? 0.5 : 0,
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255, 255, 255, 0.35)",
            borderRadius: "4px",
          },
        }}
      >
        {visibleItems.map((item, index) => (
          <Typography
            key={`${item}-${index}`}
            sx={{
              fontSize: "0.75rem",
              lineHeight: 1.45,
              color: "common.white",
              wordBreak: "break-word",
            }}
          >
            {item}
          </Typography>
        ))}
      </Box>

      {needsPreview && !expanded && (
        <Box
          component="button"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(true);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            mt: 0.75,
            p: 0,
            border: "none",
            background: "none",
            color: "common.white",
            cursor: "pointer",
            fontSize: "0.7rem",
            fontWeight: 600,
            opacity: 0.9,
            "&:hover": {
              opacity: 1,
              textDecoration: "underline",
            },
          }}
        >
          and {hiddenCount} more
          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
        </Box>
      )}

      {needsPreview && expanded && (
        <Box
          component="button"
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded(false);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            mt: 0.75,
            p: 0,
            border: "none",
            background: "none",
            color: "common.white",
            cursor: "pointer",
            fontSize: "0.7rem",
            fontWeight: 600,
            opacity: 0.9,
            "&:hover": {
              opacity: 1,
              textDecoration: "underline",
            },
          }}
        >
          Show less
          <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
        </Box>
      )}
    </Box>
  );
};

const MultiSelectFilterTooltip = ({
  items,
  isMenuOpen,
  children,
  placement = "left",
}: MultiSelectFilterTooltipProps) => (
  <Tooltip
    title={items?.length ? <MultiSelectFilterTooltipContent items={items} /> : ""}
    arrow
    placement={placement}
    disableHoverListener={!items?.length || isMenuOpen}
    disableFocusListener={isMenuOpen}
    disableInteractive={false}
    enterDelay={300}
    leaveDelay={200}
    PopperProps={multiSelectTooltipPopperProps}
    componentsProps={{
      tooltip: {
        sx: {
          maxWidth: 300,
          py: 1,
          px: 1.25,
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

export default MultiSelectFilterTooltip;
